/**
 * Catalan and Italian drop the article's vowel before a word that starts with a vowel sound, and
 * a number counts by how it is read aloud: "l’11%" (onze), "l’8%" (otto), but "el 10%" (deu).
 * Copy writes the full article before a placeholder, so the choice is made once the number is in.
 */

type ElisionRules = {
  articles: Record<string, string>;
  startsWithVowel: (value: number) => boolean;
};

// Groups of three digits, most significant first: 11_500 → [11, 500].
const digitGroups = (value: number) => {
  const groups: number[] = [];
  let rest = value;
  do {
    groups.unshift(rest % 1000);
    rest = Math.floor(rest / 1000);
  } while (rest > 0);
  return groups;
};

// u, onze · onze mil · un milió · un bilió; but mil, mil milions.
const catalanStartsWithVowel = (value: number) => {
  const [leading, ...rest] = digitGroups(value);
  const scale = rest.length;
  if (leading === 11) return true;
  if (leading === 1) return scale === 0 || scale === 2 || scale === 4;
  return false;
};

// uno, otto, undici, ottanta, ottocento, ottomila · un milione · un miliardo; but mille.
const italianStartsWithVowel = (value: number) => {
  if (String(value).startsWith("8")) return true;
  const [leading, ...rest] = digitGroups(value);
  const scale = rest.length;
  if (leading === 11) return true;
  if (leading === 1) return scale !== 1;
  return false;
};

const RULES: Record<string, ElisionRules> = {
  ca: {
    articles: { el: "l’", del: "de l’", al: "a l’", pel: "per l’" },
    startsWithVowel: catalanStartsWithVowel,
  },
  it: {
    articles: { il: "l’", del: "dell’", al: "all’", dal: "dall’", nel: "nell’", sul: "sull’" },
    startsWithVowel: italianStartsWithVowel,
  },
};

// Both languages group with "." and use "," for decimals. A dot followed by exactly three digits
// is grouping ("1.432"); any other dot is an English decimal point ("11.90").
const leadingInteger = (text: string): number | null => {
  const grouped = /^\d{1,3}(?:[.   ]\d{3})+(?!\d)/.exec(text);
  if (grouped) return Number(grouped[0].replace(/\D/g, ""));
  const plain = /^\d+/.exec(text);
  return plain ? Number(plain[0]) : null;
};

const capitalize = (text: string) => text.charAt(0).toUpperCase() + text.slice(1);

/**
 * Rewrites an article at the end of `before` when `value` is a number read with a vowel sound.
 * Returns null when nothing changes, so callers can keep their usual join.
 */
export const elideBeforeNumber = (
  locale: string | undefined,
  before: string,
  value: string
): string | null => {
  const rules = locale ? RULES[locale] : undefined;
  if (!rules) return null;
  const article = /(^|[\s(«“"])(\p{L}+) $/u.exec(before);
  if (!article) return null;
  const word = article[2];
  const elided = rules.articles[word.toLowerCase()];
  if (!elided) return null;
  const integer = leadingInteger(value);
  if (integer === null || !rules.startsWithVowel(integer)) return null;
  const replacement = word.charAt(0) === word.charAt(0).toUpperCase() ? capitalize(elided) : elided;
  return before.slice(0, before.length - word.length - 1) + replacement;
};
